import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUserShield, FaPlus, FaSearch, FaTrash, FaTimes,
  FaEnvelope, FaEye, FaEyeSlash, FaKey, FaCheck, FaBan, FaUnlock, FaCopy, FaCheckCircle,
   FaPaperPlane, FaHistory,
  FaSignInAlt, FaUserEdit, FaPowerOff, FaSpinner
} from 'react-icons/fa';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';

import { API_URL } from '../config/api';
const API = `${API_URL}/admin/admins`;

const getToken = () => localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const SB_OPEN   = 252;
const SB_CLOSED =  74;
const SB_GAP    =  12;
const NB_HEIGHT =  68;
const NB_TOP    =  12;

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtRelTime = d => {
  if (!d) return '—';
  const diff = Math.floor((Date.now() - new Date(d)) / 60000);
  if (diff < 1)    return "À l'instant";
  if (diff < 60)   return `Il y a ${diff} min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff/60)}h`;
  return `Il y a ${Math.floor(diff/1440)}j`;
};
const initials = a => {
  const parts = (a.nom_complet || `${a.prenom||''} ${a.nom||''}`).trim().split(' ');
  return `${(parts[0]||'?')[0]}${(parts[1]||'?')[0]}`.toUpperCase();
};

const STATUS_CFG = {
  active:    { label:'Actif',     color:'#198754', bg:'rgba(25,135,84,0.08)',  border:'rgba(25,135,84,0.2)',  icon:<FaCheckCircle style={{fontSize:9}}/> },
  inactive:  { label:'Désactivé', color:'#fd7e14', bg:'rgba(253,126,20,0.08)', border:'rgba(253,126,20,0.2)', icon:<FaPowerOff    style={{fontSize:9}}/> },
  suspended: { label:'Suspendu',  color:'#dc3545', bg:'rgba(220,53,69,0.08)',  border:'rgba(220,53,69,0.2)',  icon:<FaBan         style={{fontSize:9}}/> },
};

const ACTIVITY_ICONS = {
  login:    { icon:<FaSignInAlt />, color:'#198754' },
  action:   { icon:<FaUserEdit />,  color:'#fd7e14' },
  suspend:  { icon:<FaBan />,       color:'#dc3545' },
  inactive: { icon:<FaPowerOff />,  color:'#fd7e14' },
  create:   { icon:<FaUserShield />,color:'#198754' },
  email:    { icon:<FaEnvelope />,  color:'#20c997' },
};

const Toast = ({ msg, type, onClose }) => (
  <div style={{
    position:'fixed', bottom:24, right:24, zIndex:9999,
    background: type==='error' ? '#dc3545' : '#198754',
    color:'#fff', padding:'13px 20px', borderRadius:12,
    fontSize:14, fontWeight:600, boxShadow:'0 8px 24px rgba(0,0,0,0.18)',
    display:'flex', alignItems:'center', gap:10, animation:'slideUp 0.3s ease',
    maxWidth:380,
  }}>
    {type==='error' ? '❌' : '✅'} {msg}
    <button onClick={onClose} style={{ background:'none', border:'none', color:'#fff', cursor:'pointer', marginLeft:'auto', fontSize:16 }}>×</button>
    <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}`}</style>
  </div>
);

const AdminManager = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [admins,       setAdmins]       = useState([]);
  const [stats,        setStats]        = useState({ total:0, actifs:0, inactifs:0, suspendus:0 });
  const [loading,      setLoading]      = useState(true);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [showModal,    setShowModal]    = useState(false);
  const [showPass,     setShowPass]     = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [sending,      setSending]      = useState(false);
  const [actionLoading,setActionLoading]= useState(null); // id en cours d'action
  const [form,         setForm]         = useState({ nom_complet:'', email:'', password:'' });
  const [errors,       setErrors]       = useState({});
  const [activityPanel,setActivityPanel]= useState(null);
  const [toast,        setToast]        = useState(null);

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const c = isDark ? {
    bg:'#090c16', card:'rgba(255,255,255,0.04)', cardB:'rgba(255,255,255,0.08)',
    text:'#c8d4e0', textDim:'#5a6b82', title:'#eef2f7', shadow:'0 4px 24px rgba(0,0,0,0.4)',
    input:'rgba(255,255,255,0.06)', inputB:'rgba(255,255,255,0.12)',
    thead:'rgba(255,255,255,0.07)', theadText:'rgba(255,255,255,0.45)',
    rowHover:'rgba(255,255,255,0.03)', rowBorder:'rgba(255,255,255,0.06)',
    surface:'rgba(255,255,255,0.04)', modalBg:'#0f172a', overlay:'rgba(9,12,22,0.75)',
    panelBg:'#111827', panelB:'rgba(255,255,255,0.08)',
  } : {
    bg:'#f0f3f8', card:'#ffffff', cardB:'rgba(0,0,0,0.07)',
    text:'#4d6070', textDim:'#8fa3b5', title:'#1a2535', shadow:'0 2px 12px rgba(0,0,0,0.05)',
    input:'#f7f9fb', inputB:'rgba(0,0,0,0.08)',
    thead:'#1a2535', theadText:'rgba(255,255,255,0.5)',
    rowHover:'#fafbfc', rowBorder:'rgba(0,0,0,0.055)',
    surface:'rgba(0,0,0,0.03)', modalBg:'#ffffff', overlay:'rgba(15,23,42,0.55)',
    panelBg:'#f7f9fb', panelB:'rgba(0,0,0,0.07)',
  };

  // ── Notifications toast ───────────────────────────────────────────────────
  const showToast = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Chargement des admins ─────────────────────────────────────────────────
  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, {
        params: { search: searchQuery || undefined },
        ...authHeaders(),
      });
      setAdmins(res.data.data);
      setStats(res.data.stats);
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors du chargement.', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const delay = setTimeout(fetchAdmins, 300);
    return () => clearTimeout(delay);
  }, [fetchAdmins]);

  // ── Créer un admin ────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.nom_complet.trim()) e.nom_complet = 'Requis';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!form.password.trim()) e.password = 'Requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      const res = await axios.post(API, form, authHeaders());
      setAdmins(p => [...p, res.data.data]);
      setStats(p => ({ ...p, total: p.total+1, actifs: p.actifs+1 }));
      setShowModal(false);
      showToast(`Compte créé — email envoyé à ${form.email}`);
    } catch (err) {
      const msg = err.response?.data?.message
               || Object.values(err.response?.data?.errors || {})[0]?.[0]
               || 'Erreur lors de la création.';
      showToast(msg, 'error');
    } finally {
      setSending(false);
    }
  };

  // ── Changer statut ────────────────────────────────────────────────────────
  const setStatus = async (id, status) => {
    setActionLoading(id + '_status');
    try {
      const res = await axios.patch(`${API}/${id}/status`, { status }, authHeaders());
      setAdmins(p => p.map(a => a.db_id === id ? { ...a, ...res.data.data } : a));
      const labels = { active:'réactivé', inactive:'désactivé', suspended:'suspendu' };
      showToast(`Compte ${labels[status]}.`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Renvoyer identifiants ─────────────────────────────────────────────────
  const resend = async (admin) => {
    setActionLoading(admin.db_id + '_resend');
    try {
      await axios.post(`${API}/${admin.db_id}/resend-credentials`, {}, authHeaders());
      showToast(`Nouveaux identifiants envoyés à ${admin.email}`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur envoi email.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Supprimer ─────────────────────────────────────────────────────────────
  const deleteAdmin = async (id) => {
    if (!window.confirm('Supprimer définitivement cet administrateur ?')) return;
    setActionLoading(id + '_delete');
    try {
      await axios.delete(`${API}/${id}`, authHeaders());
      setAdmins(p => p.filter(a => a.db_id !== id));
      if (activityPanel === id) setActivityPanel(null);
      showToast('Administrateur supprimé.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la suppression.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(form.password).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const openModal = () => {
    setForm({ nom_complet:'', email:'', password: genPassword() });
    setErrors({});
    setShowPass(false);
    setCopied(false);
    setShowModal(true);
  };

  const panelAdmin = admins.find(a => a.db_id === activityPanel || a.id === activityPanel);
  const mainML = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT = `${NB_TOP + NB_HEIGHT + 12}px`;

  const fieldLabel = txt => (
    <label style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', color:c.textDim, display:'block', marginBottom:7 }}>{txt}</label>
  );
  const inputBase = key => ({
    width:'100%', height:44, padding:'0 14px',
    border:`1.5px solid ${errors[key] ? '#dc3545' : c.inputB}`,
    borderRadius:10, fontSize:14, fontWeight:500,
    outline:'none', background:c.input, color:c.title,
    transition:'border-color 0.2s', fontFamily:"'Plus Jakarta Sans',sans-serif",
  });

  return (
    <div style={{ background:c.bg, minHeight:'100vh', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;font-family:'Plus Jakarta Sans',sans-serif;}
        .am-main{transition:margin-left 0.28s cubic-bezier(0.4,0,0.2,1);min-height:100vh;}
        .am-body{padding:24px 26px 48px;}
        .am-trow{border-bottom:1px solid;transition:background 0.14s;}
        .am-trow:last-child{border-bottom:none;}
        .am-ab{width:33px;height:33px;border-radius:8px;border:1.5px solid;display:flex;align-items:center;justify-content:center;font-size:12px;cursor:pointer;transition:all 0.18s;background:transparent;}
        .am-ab:disabled{opacity:0.4;cursor:not-allowed;}
        .am-ab-g{border-color:rgba(25,135,84,0.2);color:#198754;}
        .am-ab-g:hover:not(:disabled){background:rgba(25,135,84,0.08);border-color:#198754;}
        .am-ab-o{border-color:rgba(253,126,20,0.2);color:#fd7e14;}
        .am-ab-o:hover:not(:disabled){background:rgba(253,126,20,0.08);border-color:#fd7e14;}
        .am-ab-r{border-color:rgba(220,53,69,0.2);color:#dc3545;}
        .am-ab-r:hover:not(:disabled){background:rgba(220,53,69,0.08);border-color:#dc3545;}
        .am-ab-dim{border-color:rgba(0,0,0,0.08);color:#8fa3b5;}
        .am-ab-dim:hover:not(:disabled){background:rgba(0,0,0,0.04);}
        .am-overlay{position:fixed;inset:0;backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px;animation:fadeIn 0.2s ease;}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .am-popup{border-radius:18px;width:100%;max-width:440px;box-shadow:0 24px 60px rgba(0,0,0,0.22);animation:popIn 0.26s cubic-bezier(0.34,1.56,0.64,1);overflow:hidden;}
        @keyframes popIn{from{opacity:0;transform:scale(0.9) translateY(20px)}to{opacity:1;transform:none}}
        .am-panel-overlay{position:fixed;inset:0;z-index:1500;display:flex;justify-content:flex-end;animation:fadeIn 0.2s ease;}
        .am-panel-scrim{position:absolute;inset:0;background:rgba(15,23,42,0.35);backdrop-filter:blur(3px);}
        .am-panel{position:relative;width:380px;height:100%;overflow-y:auto;box-shadow:-8px 0 32px rgba(0,0,0,0.18);display:flex;flex-direction:column;animation:slideIn 0.28s cubic-bezier(0.4,0,0.2,1);}
        @keyframes slideIn{from{transform:translateX(100%)}to{transform:none}}
        .am-pass-wrap{display:flex;align-items:center;border:1.5px solid;border-radius:10px;overflow:hidden;transition:border-color 0.2s;}
        .am-pass-wrap:focus-within{border-color:#198754!important;}
        .am-pass-inp{flex:1;height:44px;padding:0 14px;border:none;outline:none;font-size:14px;font-weight:600;background:transparent;letter-spacing:0.5px;}
        .am-pico{width:42px;height:44px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#8fa3b5;transition:color 0.2s;flex-shrink:0;}
        .am-pico:hover{color:#198754;}
        .am-search-box{display:flex;align-items:center;gap:10px;border:1.5px solid;border-radius:10px;padding:0 16px;height:42px;transition:border-color 0.2s;width:320px;}
        .am-search-box:focus-within{border-color:#198754!important;}
        .am-search-box input{flex:1;border:none;outline:none;font-size:14px;background:transparent;}
        .am-btn-p{display:inline-flex;align-items:center;gap:8px;padding:11px 22px;background:#198754;color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;}
        .am-btn-p:hover:not(:disabled){background:#146c43;transform:translateY(-1px);box-shadow:0 4px 14px rgba(25,135,84,0.3);}
        .am-btn-p:disabled{opacity:0.7;cursor:wait;}
        .am-btn-g{display:inline-flex;align-items:center;gap:8px;padding:11px 18px;background:transparent;border:1.5px solid;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;}
        .am-pill{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:20px;font-size:11.5px;font-weight:600;border:1px solid;}
        .am-cx{width:34px;height:34px;border-radius:8px;border:1.5px solid;background:transparent;display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;transition:all 0.18s;}
        .am-cx:hover{background:#dc3545!important;color:#fff!important;border-color:#dc3545!important;}
        .am-regen{font-size:11.5px;font-weight:600;color:#fd7e14;background:rgba(253,126,20,0.08);border:1px solid rgba(253,126,20,0.2);border-radius:6px;padding:3px 9px;cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:all 0.18s;}
        .am-regen:hover{background:rgba(253,126,20,0.15);}
        .am-status-btn{display:inline-flex;align-items:center;gap:7px;padding:8px 14px;border-radius:9px;font-size:12.5px;font-weight:600;cursor:pointer;border:1.5px solid;transition:all 0.18s;}
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:768px){.am-body{padding:14px 14px 32px}.am-popup{max-width:100%;border-radius:20px 20px 0 0;align-self:flex-end;margin:auto 0 0}.am-search-box{width:100%}.am-panel{width:100%}}
      `}</style>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Administrateurs" adminName="Jean Dupont" />

      <main className="am-main" style={{ marginLeft:mainML, paddingTop:mainPT }}>
        <div className="am-body">

          {/* En-tête */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, gap:16, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:13, background:'#198754', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                <FaUserShield />
              </div>
              <div>
                <h1 style={{ fontSize:20, fontWeight:700, color:c.title, marginBottom:2 }}>Gestion des administrateurs</h1>
                <p style={{ fontSize:13, color:c.textDim }}>
                  {stats.actifs} actif{stats.actifs>1?'s':''} · {stats.total} au total
                </p>
              </div>
            </div>
            <button className="am-btn-p" onClick={openModal}><FaPlus /> Créer un administrateur</button>
          </div>

          {/* Stats */}
          <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
            {[
              { label:'Total',     val:stats.total,     color:'#0d6efd' },
              { label:'Actifs',    val:stats.actifs,    color:'#198754' },
              { label:'Inactifs',  val:stats.inactifs,  color:'#fd7e14' },
              { label:'Suspendus', val:stats.suspendus, color:'#dc3545' },
            ].map(s => (
              <div key={s.label} style={{ background:c.card, border:`1px solid ${c.cardB}`, borderRadius:12, padding:'14px 20px', minWidth:120, boxShadow:c.shadow }}>
                <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.val}</div>
                <div style={{ fontSize:12, color:c.textDim, fontWeight:600, marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Recherche */}
          <div style={{ marginBottom:16 }}>
            <div className="am-search-box" style={{ background:c.card, borderColor:c.cardB }}>
              <FaSearch style={{ color:c.textDim, fontSize:13, flexShrink:0 }} />
              <input placeholder="Rechercher par nom ou email…" value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)} style={{ color:c.title }} />
              {searchQuery && (
                <FaTimes style={{ color:c.textDim, cursor:'pointer', fontSize:12 }}
                  onClick={() => setSearchQuery('')} />
              )}
            </div>
          </div>

          {/* Tableau */}
          <div style={{ background:c.card, borderRadius:14, border:`1px solid ${c.cardB}`, boxShadow:c.shadow, overflow:'hidden' }}>
            {loading ? (
              <div style={{ textAlign:'center', padding:'56px 20px', color:c.textDim }}>
                <FaSpinner style={{ fontSize:32, display:'block', margin:'0 auto 12px', animation:'spin 1s linear infinite' }} />
                <p style={{ fontSize:13 }}>Chargement…</p>
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:720 }}>
                  <thead>
                    <tr style={{ background:c.thead }}>
                      {['Administrateur','Email','Connexion','Statut','Actions'].map((h,i) => (
                        <th key={i} style={{ padding:'13px 20px', textAlign:i===4?'center':'left', fontSize:10.5, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.7px', color:c.theadText, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign:'center', padding:'56px 20px', color:c.textDim }}>
                          <FaUserShield style={{ fontSize:40, display:'block', margin:'0 auto 12px', opacity:0.25 }} />
                          <p style={{ fontSize:14, fontWeight:600 }}>Aucun administrateur trouvé</p>
                        </td>
                      </tr>
                    ) : admins.map(adm => {
                      const st  = STATUS_CFG[adm.status] || STATUS_CFG.active;
                      const dbId= adm.db_id || adm.id;
                      return (
                        <tr key={adm.id} className="am-trow" style={{ borderBottomColor:c.rowBorder }}
                          onMouseEnter={e => e.currentTarget.style.background=c.rowHover}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>

                          <td style={{ padding:'14px 20px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                              <div style={{ width:38, height:38, borderRadius:10, background:'#198754', color:'#fff', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', position:'relative', flexShrink:0 }}>
                                {initials(adm)}
                                <div style={{ position:'absolute', bottom:-2, right:-2, width:11, height:11, borderRadius:'50%', background:adm.isOnline ? '#198754' : '#8fa3b5', border:`2.5px solid ${c.card}` }} />
                              </div>
                              <div>
                                <div style={{ fontSize:13.5, fontWeight:600, color:c.title }}>{adm.nom_complet || `${adm.prenom} ${adm.nom}`}</div>
                                <div style={{ fontSize:11, color:c.textDim, marginTop:1 }}>{adm.id}</div>
                              </div>
                            </div>
                          </td>

                          <td style={{ padding:'14px 20px' }}>
                            <span style={{ fontSize:13.5, color:c.text }}>{adm.email}</span>
                          </td>

                          <td style={{ padding:'14px 20px' }}>
                            {adm.lastLogin
                              ? <div>
                                  <div style={{ fontSize:13, fontWeight:600, color:c.title }}>{fmtDate(adm.lastLogin)}</div>
                                  <div style={{ fontSize:11.5, color:c.textDim, marginTop:1 }}>{fmtRelTime(adm.lastLogin)}</div>
                                </div>
                              : <span style={{ fontSize:12.5, color:c.textDim, fontStyle:'italic' }}>Jamais connecté</span>
                            }
                          </td>

                          <td style={{ padding:'14px 20px' }}>
                            <span className="am-pill" style={{ color:st.color, background:st.bg, borderColor:st.border }}>
                              {st.icon} {st.label}
                            </span>
                          </td>

                          <td style={{ padding:'14px 20px' }}>
                            <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                              <button className="am-ab am-ab-dim" title="Activités récentes"
                                onClick={() => setActivityPanel(activityPanel === dbId ? null : dbId)}>
                                <FaHistory />
                              </button>

                              <button className="am-ab am-ab-g" title="Renvoyer les accès"
                                disabled={actionLoading === dbId+'_resend'}
                                onClick={() => resend(adm)}>
                                {actionLoading === dbId+'_resend'
                                  ? <FaSpinner style={{ animation:'spin 1s linear infinite' }} />
                                  : <FaEnvelope />
                                }
                              </button>

                              {adm.status === 'active' && <>
                                <button className="am-ab am-ab-o" title="Désactiver"
                                  disabled={!!actionLoading}
                                  onClick={() => setStatus(dbId,'inactive')}><FaPowerOff /></button>
                                <button className="am-ab am-ab-r" title="Suspendre"
                                  disabled={!!actionLoading}
                                  onClick={() => setStatus(dbId,'suspended')}><FaBan /></button>
                              </>}
                              {adm.status === 'inactive' && <>
                                <button className="am-ab am-ab-g" title="Réactiver"
                                  disabled={!!actionLoading}
                                  onClick={() => setStatus(dbId,'active')}><FaUnlock /></button>
                                <button className="am-ab am-ab-r" title="Suspendre"
                                  disabled={!!actionLoading}
                                  onClick={() => setStatus(dbId,'suspended')}><FaBan /></button>
                              </>}
                              {adm.status === 'suspended' && (
                                <button className="am-ab am-ab-g" title="Réactiver"
                                  disabled={!!actionLoading}
                                  onClick={() => setStatus(dbId,'active')}><FaUnlock /></button>
                              )}

                              <button className="am-ab am-ab-r" title="Supprimer"
                                disabled={actionLoading === dbId+'_delete'}
                                onClick={() => deleteAdmin(dbId)}>
                                {actionLoading === dbId+'_delete'
                                  ? <FaSpinner style={{ animation:'spin 1s linear infinite' }} />
                                  : <FaTrash />
                                }
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Panel activités */}
      {activityPanel && panelAdmin && (
        <div className="am-panel-overlay">
          <div className="am-panel-scrim" onClick={() => setActivityPanel(null)} />
          <div className="am-panel" style={{ background:c.panelBg, borderLeft:`1px solid ${c.panelB}` }}>
            <div style={{ padding:'20px 22px', borderBottom:`1px solid ${c.panelB}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:c.card }}>
              <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:'#198754', color:'#fff', fontSize:13, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {initials(panelAdmin)}
                </div>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:c.title }}>{panelAdmin.nom_complet || `${panelAdmin.prenom} ${panelAdmin.nom}`}</div>
                  <div style={{ fontSize:11.5, color:c.textDim }}>Activités récentes</div>
                </div>
              </div>
              <button className="am-cx" style={{ borderColor:c.panelB, color:c.textDim }} onClick={() => setActivityPanel(null)}><FaTimes /></button>
            </div>

            <div style={{ flex:1, padding:'20px 22px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:c.textDim }}>
              <FaHistory style={{ fontSize:40, opacity:0.2, marginBottom:12 }} />
              <p style={{ fontSize:13, fontWeight:600 }}>Historique non disponible</p>
              <p style={{ fontSize:12, marginTop:4 }}>À connecter avec une table activity_logs</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal création */}
      {showModal && (
        <div className="am-overlay" style={{ background:c.overlay }} onClick={() => setShowModal(false)}>
          <div className="am-popup" style={{ background:c.modalBg }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'20px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:`1px solid ${c.cardB}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:11, background:'rgba(25,135,84,0.1)', border:'1.5px solid rgba(25,135,84,0.2)', color:'#198754', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17 }}>
                  <FaUserShield />
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:c.title }}>Nouvel administrateur</div>
                  <div style={{ fontSize:12, color:c.textDim }}>Les identifiants seront envoyés par email</div>
                </div>
              </div>
              <button className="am-cx" style={{ borderColor:c.cardB, color:c.textDim }} onClick={() => setShowModal(false)}><FaTimes /></button>
            </div>

            <div style={{ padding:'20px 22px', display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                {fieldLabel('Nom complet *')}
                <input style={inputBase('nom_complet')} placeholder="Prénom Nom" value={form.nom_complet}
                  onChange={e => setForm(p => ({...p, nom_complet:e.target.value}))} />
                {errors.nom_complet && <span style={{ fontSize:11, color:'#dc3545', marginTop:4, display:'block' }}>{errors.nom_complet}</span>}
              </div>

              <div>
                {fieldLabel('Adresse email *')}
                <input style={inputBase('email')} type="email" placeholder="prenom.nom@cinef.org" value={form.email}
                  onChange={e => setForm(p => ({...p, email:e.target.value}))} />
                {errors.email && <span style={{ fontSize:11, color:'#dc3545', marginTop:4, display:'block' }}>{errors.email}</span>}
              </div>

              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:7 }}>
                  {fieldLabel('Mot de passe temporaire *')}
                  <button className="am-regen" onClick={() => setForm(p => ({...p, password: genPassword()}))}>
                    <FaKey style={{ fontSize:9 }} /> Régénérer
                  </button>
                </div>
                <div className="am-pass-wrap" style={{ borderColor:errors.password ? '#dc3545' : c.inputB, background:c.input }}>
                  <input className="am-pass-inp" type={showPass ? 'text' : 'password'} value={form.password}
                    onChange={e => setForm(p => ({...p, password:e.target.value}))} style={{ color:c.title }} />
                  <div className="am-pico" onClick={() => setShowPass(v => !v)}>{showPass ? <FaEyeSlash /> : <FaEye />}</div>
                  <div className="am-pico" style={{ color:copied ? '#198754' : undefined }} onClick={copyPassword} title="Copier">
                    {copied ? <FaCheck /> : <FaCopy />}
                  </div>
                </div>
                {errors.password && <span style={{ fontSize:11, color:'#dc3545', marginTop:4, display:'block' }}>{errors.password}</span>}
              </div>

              <div style={{ padding:'11px 14px', background:'rgba(25,135,84,0.05)', border:'1px solid rgba(25,135,84,0.15)', borderRadius:10, display:'flex', gap:10 }}>
                <FaEnvelope style={{ color:'#198754', flexShrink:0, marginTop:2 }} />
                <span style={{ fontSize:12.5, color:c.text, lineHeight:1.8 }}>
                  Un email sera envoyé à <strong style={{ color:c.title }}>{form.email || '…'}</strong> avec les identifiants.
                </span>
              </div>
            </div>

            <div style={{ padding:'0 22px 20px', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="am-btn-g" style={{ borderColor:c.cardB, color:c.textDim }} onClick={() => setShowModal(false)}>Annuler</button>
              <button className="am-btn-p" onClick={handleCreate} disabled={sending}>
                {sending
                  ? <><FaSpinner style={{ animation:'spin 1s linear infinite' }} /> Création…</>
                  : <><FaPaperPlane /> Créer et envoyer</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManager;